import React from "react";
import { MapPin, Phone, MessageCircle, Facebook, Instagram, Youtube, Clock, ExternalLink } from "lucide-react";
import { useSite } from "@/context/ThemeContext";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const { data } = useSite();
  if (!data) return null;
  const { branches, businessHours, socials } = data.content;
  const wa = (socials?.whatsapp || "9272980098").replace(/\D/g, "");

  return (
    <div className="relative z-10 pt-28">
      <section className="mx-auto max-w-7xl px-5 py-12" data-testid="contact-section">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-c">Contact Us</span>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-t mt-4">Visit Us in <span className="text-primary-c">Roha.</span></h1>
          <p className="text-muted-c text-lg mt-5 max-w-2xl">Two branches to serve you. Drop by, call or message us on WhatsApp.</p>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2 mt-12">
          {(branches || []).map((b, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="glass card-hover rounded-3xl p-8 h-full flex flex-col" data-testid={`branch-${i}`}>
                <h2 className="font-display font-bold text-2xl text-t">{b.name}</h2>
                <p className="text-muted-c mt-4 flex items-start gap-3"><MapPin className="text-primary-c mt-0.5 shrink-0" size={18} /> {b.address}</p>
                <a href={`tel:${b.phone}`} className="text-t mt-3 flex items-center gap-3 hover:text-primary-c transition-colors"><Phone className="text-primary-c shrink-0" size={18} /> {b.phone}</a>
                <div className="flex flex-wrap gap-3 mt-6">
                  <a href={`https://wa.me/91${wa}`} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm" data-testid={`whatsapp-${i}`}>
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                  {b.mapUrl && (
                    <a href={b.mapUrl} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm" data-testid={`map-link-${i}`}>
                      <ExternalLink size={16} /> Open in Maps
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Map embeds */}
        <div className="grid gap-6 md:grid-cols-2 mt-8" data-testid="map-embed">
          {(branches || []).map((b, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="glass rounded-3xl p-3">
                <div className="px-3 py-2 font-display font-bold text-t">{b.name}</div>
                <iframe title={b.name} src={b.embed || `https://www.google.com/maps?q=${encodeURIComponent(b.address)}&output=embed`}
                  className="w-full h-[300px] rounded-2xl border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Hours + socials */}
        <div className="grid gap-6 md:grid-cols-2 mt-8">
          <Reveal>
            <div className="glass rounded-3xl p-8" data-testid="business-hours">
              <div className="flex items-center gap-3 mb-3"><Clock className="text-primary-c" size={22} /><h3 className="font-display font-bold text-xl text-t">Business Hours</h3></div>
              <p className="text-muted-c">{businessHours}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-8" data-testid="contact-socials">
              <h3 className="font-display font-bold text-xl text-t mb-4">Follow Us</h3>
              <div className="flex gap-3">
                <a href={socials?.facebook || "https://facebook.com/bitsNbytes.roha"} target="_blank" rel="noreferrer" className="p-3 rounded-full btn-ghost"><Facebook size={20} /></a>
                <a href={socials?.instagram || "#"} target="_blank" rel="noreferrer" className="p-3 rounded-full btn-ghost"><Instagram size={20} /></a>
                <a href={socials?.youtube || "#"} target="_blank" rel="noreferrer" className="p-3 rounded-full btn-ghost"><Youtube size={20} /></a>
                <a href={`https://wa.me/91${wa}`} target="_blank" rel="noreferrer" className="p-3 rounded-full btn-ghost"><MessageCircle size={20} /></a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
